import { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { QRCodeSVG } from 'qrcode.react';
import { X, Copy, Check } from 'lucide-react';
import { useClipboardCopy } from '../hooks/useClipboardCopy';

function QRModalCorners({ colour }) {
  const base = { position: 'absolute', width: 16, height: 16 };
  return (
    <>
      <span style={{ ...base, top: -1, left: -1, borderTop: `2px solid ${colour}`, borderLeft: `2px solid ${colour}` }} />
      <span style={{ ...base, top: -1, right: -1, borderTop: `2px solid ${colour}`, borderRight: `2px solid ${colour}` }} />
      <span style={{ ...base, bottom: -1, left: -1, borderBottom: `2px solid ${colour}`, borderLeft: `2px solid ${colour}` }} />
      <span style={{ ...base, bottom: -1, right: -1, borderBottom: `2px solid ${colour}`, borderRight: `2px solid ${colour}` }} />
    </>
  );
}

function QRModalHeader({ coin, onClose }) {
  return (
    <div className="w-full flex items-center justify-between">
      <span className="font-mono text-xs tracking-widest" style={{ color: coin.colour }}>
        // {coin.name.toUpperCase()}_ADDRESS
      </span>
      <button
        onClick={onClose}
        data-testid="qr-modal-close"
        className="text-white/30 hover:text-white/80 transition-colors p-1"
      >
        <X size={14} />
      </button>
    </div>
  );
}

function QRModalCode({ coin }) {
  return (
    <div
      className="p-3 relative"
      style={{ background: '#0a0a0a', border: `1px solid ${coin.colour}44`, boxShadow: `0 0 20px ${coin.colour}22` }}
    >
      <QRCodeSVG
        value={coin.address}
        size={200}
        bgColor="#0a0a0a"
        fgColor={coin.colour}
        level="H"
        data-testid={`qr-code-${coin.ticker.toLowerCase()}`}
      />
      <div className="absolute inset-0 pointer-events-none qr-scanline" />
    </div>
  );
}

function QRModalAddress({ coin }) {
  return (
    <div className="w-full px-3 py-2" style={{ background: '#0d0d0d', border: `1px dashed ${coin.colour}33` }}>
      <p
        className="font-mono text-[9px] text-white/50 break-all leading-relaxed text-center"
        data-testid={`qr-address-${coin.ticker.toLowerCase()}`}
      >
        {coin.address}
      </p>
    </div>
  );
}

function QRModalCopyButton({ coin, copied, onCopy }) {
  return (
    <button
      onClick={onCopy}
      data-testid={`qr-copy-${coin.ticker.toLowerCase()}`}
      className="w-full py-2.5 font-mono text-xs tracking-widest transition-all duration-200 flex items-center justify-center gap-2"
      style={{
        background: copied ? `${coin.colour}22` : 'transparent',
        border: `1px solid ${coin.colour}`,
        color: coin.colour,
      }}
    >
      {copied
        ? <><Check size={12} />COPIED</>
        : <><Copy size={12} />[ COPY_ADDRESS ]</>
      }
    </button>
  );
}

export function QRModal({ coin, onClose }) {
  const { copied, copy } = useClipboardCopy();

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  return (
    <AnimatePresence>
      <motion.div
        key="backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        style={{ backgroundColor: 'rgba(0,0,0,0.92)' }}
        onClick={onClose}
        data-testid="qr-modal-backdrop"
      >
        <motion.div
          key="card"
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ duration: 0.28, ease: 'easeOut' }}
          className="relative w-full max-w-sm mx-auto"
          onClick={(e) => e.stopPropagation()}
          data-testid="qr-modal-card"
          style={{
            background: '#050505',
            border: `1px solid ${coin.colour}`,
            boxShadow: `0 0 32px ${coin.colour}33, 0 0 64px ${coin.colour}11, inset 0 0 20px ${coin.colour}08`,
          }}
        >
          <QRModalCorners colour={coin.colour} />

          <div className="p-6 flex flex-col items-center gap-5">
            <QRModalHeader coin={coin} onClose={onClose} />
            <QRModalCode coin={coin} />

            <div className="text-center">
              <p className="font-mono text-[10px] tracking-[0.25em] text-white/30 uppercase mb-1">
                {coin.ticker} · {coin.note}
              </p>
            </div>

            <QRModalAddress coin={coin} />
            <QRModalCopyButton coin={coin} copied={copied} onCopy={() => copy(coin.address)} />

            <button
              onClick={onClose}
              className="font-mono text-[10px] text-white/20 hover:text-white/50 tracking-widest transition-colors"
            >
              &lt;&lt; TERMINATE_SESSION
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
